<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/features/auth/stores/authStore.js';
import { useDisplayScale } from '@/composables/useDisplayScale.js';
import { exitProgram } from '@/utils/exitProgram.js';

const { scale, options: scaleOptions } = useDisplayScale();
const authStore = useAuthStore();
// 以 store 狀態判斷登入與否：localStorage 非響應式，登入後不 reload 不會更新；
// accessToken 只存在記憶體，reload 後靠 persist 的 username 判斷
const isLoggedIn = computed(
  () => !!(authStore.accessToken || authStore.username),
);

// 登入者顯示 /api/staff/me 的姓名，尚未取得時退回登入回應的 displayName
const loginUserName = computed(
  () => authStore.staffName || authStore.displayName,
);
// 預設醫師：姓名 + 代號（如「張德肅 05」）
const defaultDoctor = computed(() =>
  [authStore.staffName, authStore.staffSerial].filter(Boolean).join(' '),
);

const menuBar = [
  'A 與醫聖系統連線',
  'S 系統工具',
  'I 工具程式',
  'D 資料庫',
  'T 統計分析',
  'P 參數設定',
  'C 關懷系統',
  'F 便捷功能',
  'O 其他功能',
  '翰鼎數位專案',
  'C 仩詮網掛轉入',
  'X 結束系統',
];

// 登入頁尚未登入，不顯示「結束系統」
const route = useRoute();
const visibleMenuBar = computed(() =>
  route.path === '/login'
    ? menuBar.filter((item) => item !== 'X 結束系統')
    : menuBar,
);

const handleMenuClick = (item, event) => {
  if (item === 'X 結束系統') {
    event.preventDefault();
    exitProgram();
  }
};
</script>
<script>
export default {
  name: 'DefaultLayout',
};
</script>

<template>
  <div class="relative h-full flex flex-col bg-white">
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-90"
      style="background-image: url('/image/backgroundImg.webp')"
    ></div>
    <div class="relative flex flex-row gap-3 bg-gray-300 text-black px-2">
      <router-link
        to="/"
        v-for="(item, index) of visibleMenuBar"
        :id="index"
        :key="`menuBar-${index}`"
        class="hover:underline hover:underline-offset-2"
        @click="handleMenuClick(item, $event)"
        >{{ item }}</router-link
      >
    </div>
    <!-- min-h-0 + 捲動：顯示比例放大後內容超出時在此捲動，避免底部狀態列（含顯示比例選單）被擠出畫面 -->
    <div class="relative flex-1 min-h-0 overflow-y-auto">
      <div
        class="w-2/5 h-fit my-3 ml-2 p-4 shadow-3xl bg-gradient-to-b from-purple-200 via-purple-300 to-purple-400"
      >
        <span class="text-[2.5rem] tracking-widest text-white font-bold"
          >醫聖診療系統</span
        >
      </div>
      <div
        class="absolute top-4 right-4 flex items-start gap-3 z-10 font-bold whitespace-nowrap w-max"
      >
        <img src="/image/logo.webp" alt="logo" class="w-14 h-14 shrink-0" />
        <div class="flex flex-col text-white w-max">
          <span
            class="text-[3rem] tracking-widest leading-tight whitespace-nowrap"
            >仩詮資訊有限公司</span
          >
          <span
            class="tracking-widest text-xl leading-tight flex flex-row flex-nowrap justify-between whitespace-nowrap"
          >
            <span>醫聖診療系統</span>
            <span class="text-yellow-300 ml-2">親切 專業 誠信 永續</span>
          </span>
          <span
            class="text-xl text-white leading-tight text-right tracking-wider whitespace-nowrap"
            >一年365天，天天都隨伴您！</span
          >
          <div
            class="flex items-center gap-2 text-md text-gray-300 mt-1 flex-nowrap whitespace-nowrap"
          >
            <span class="flex-1 h-px bg-gray-400"></span>
            <span class="whitespace-nowrap">醫心行千里・聖德揚萬世</span>
            <span class="flex-1 h-px bg-gray-400"></span>
          </div>
        </div>
      </div>
      <router-view :key="$route.path"></router-view>
    </div>

    <div class="relative flex flex-row gap-3 bg-gray-300 text-black px-2">
      <span>＊醫聖 醫療院所作業系統</span>
      <span>＊123344848.39393939</span>
      <span>Tel: (07)315-4932</span>
      <template v-if="isLoggedIn">
        <span>【登入者: {{ loginUserName }}】</span>
        <span>【預設醫師: {{ defaultDoctor }}】</span>
      </template>
      <div class="ml-auto flex flex-row gap-1 items-center">
        <span>顯示比例</span>
        <select
          v-model.number="scale"
          class="text-black bg-white border border-gray-400 px-1"
        >
          <option v-for="opt in scaleOptions" :key="opt" :value="opt">
            {{ Math.round(opt * 100) }}%
          </option>
        </select>
      </div>
    </div>
  </div>
</template>
